import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {filter, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {ApplePayload, Credentials, FacebookPayload, GooglePayload, LoginCredentials, OtpChannel, OtpOptionsResponse, PasswordRecovery, SignupPayload, SignUpResponse} from '../models/Authentication';
import {UpgradeInfoResponse, UserModel} from '../models/User';
import {Observable} from 'rxjs';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';

const AUTH_URL = '/api/auth';
const SESSION_URL = `${AUTH_URL}/session`;
const LOGOUT_URL = `${AUTH_URL}/logout`;
const PASSWORD_RECOVER_URL = `${AUTH_URL}/recover`;
const PASSWORD_RESET_URL = `${AUTH_URL}/reset`
const PASSWORD_UPDATE_URL = `${AUTH_URL}/update-password`;
const VALIDATE_TOKEN_URL = `${AUTH_URL}/token`
const INTERCOM_TOKEN_URL = `${AUTH_URL}/intercom/token`
const USER_INFO_UPGRADE_URL = `${AUTH_URL}/user-info`


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    user: UserModel | null = null;
    private userSubject = new BehaviorSubject<UserModel | null>(this.loadUserFromStorage());
    private sessionRequest: Observable<UserModel> | null = null;
    public user$ = this.userSubject.asObservable();

    constructor(private http: HttpClient,
                private router: Router) {
    }

    signUp(payload: SignupPayload): Observable<UserModel> {
        return this.http.post<SignUpResponse>(`${AUTH_URL}/signup`, payload, { withCredentials: true }).pipe(
            filter((response: UserModel) => !!response.token),
            map(response => this.processUser(response))
        );
    }


    login(credentials: LoginCredentials): Observable<UserModel> {
        return this.http.post<UserModel>(`${AUTH_URL}/login`, credentials, {withCredentials: true}).pipe(
            map((res: UserModel) => this.processUser(res))
        );
    }

    signupWithGoogle(credentials: GooglePayload): Observable<UserModel> {
        return this.http.post<SignUpResponse>(`${AUTH_URL}/signup/google`, credentials, { withCredentials: true }).pipe(
            filter((response: UserModel) => !!response.token),
            map(response => this.processUser(response))
        );
    }


    loginWithGoogle(credentials: Credentials): Observable<UserModel> {
        return this.http.post<{user: UserModel}>(`${AUTH_URL}/login/google`, credentials, { withCredentials: true }).pipe(
            map(response => this.processUser(response.user))
        );
    }

    signupWithFacebook(body: FacebookPayload): Observable<UserModel> {
        return this.http.post<SignUpResponse>(`${AUTH_URL}/signup/facebook`, body, { withCredentials: true }).pipe(
            filter((response: UserModel) => !!response.token),
            map(response => this.processUser(response))
        );
    }

    loginWithFacebook(token: string): Observable<UserModel> {
        return this.http.post<{user: UserModel}>(`${AUTH_URL}/login/facebook`, {token}, { withCredentials: true }).pipe(
            map(response => this.processUser(response.user))
        );
    }

    signupWithApple(body: ApplePayload): Observable<UserModel> {
        return this.http.post<SignUpResponse>(`${AUTH_URL}/signup/apple`, body, { withCredentials: true }).pipe(
            filter((response: UserModel) => !!response.token),
            map(response => this.processUser(response))
        );
    }

    loginWithApple(token: string): Observable<UserModel> {
        return this.http.post<{user: UserModel}>(`${AUTH_URL}/login/apple`, {token}, { withCredentials: true }).pipe(
            map(response => this.processUser(response.user))
        );
    }


    logout(): Observable<void> {
        return this.http.post<void>(LOGOUT_URL, {},  {withCredentials: true}).pipe(
            tap(() => {
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('user');
                this.sessionRequest = null;
                this.userSubject.next(null);
                this.router.navigate(['/login']);
            })
        );
    }


    recoverPassword(email: string): Observable<{message: string}> {
        return this.http.post<{message: string}>(PASSWORD_RECOVER_URL, {email});
    }

    validateUserToken(token: string): Observable<Object> {
        return this.http.post(VALIDATE_TOKEN_URL, {token})
    }

    resetPassword(token: string, password: string): Observable<{message: string}> {
        return this.http.post<{message: string}>(PASSWORD_RESET_URL, {token, password})
    }

    get token(): string | null {
        return sessionStorage.getItem('token');
    }

    // Applies a fresher UserModel the caller already has in hand (e.g. an
    // upgrade response body) without a round trip to /auth/session.
    applyUpdatedUser(raw: Partial<UserModel>): UserModel {
        return this.processUser(raw);
    }

    //allow for partials here for non-authenticated users on upgrade page
    //set as merge to not overwrite existing 'good values' if a partial user model is updated (phone, name, etc.)
    private processUser(raw: Partial<UserModel>): UserModel {
        const current = this.userSubject.getValue();
        const merged = mergeUserSafe(current, raw)
        if (merged.token) {
            sessionStorage.setItem('token', merged.token);
            sessionStorage.setItem('user', JSON.stringify(merged));
        }
        this.userSubject.next(merged);
        return merged;
    }

    private loadUserFromStorage(): UserModel | null {
        const userData = sessionStorage.getItem('user');
        return userData ? new UserModel(JSON.parse(userData)) : null;
    }

    loadSession(forceRefresh = false): Observable<UserModel> {
        if (forceRefresh) {
            this.sessionRequest = null;
        }
        if (!this.sessionRequest) {
            this.sessionRequest = this.http
                .get<UserModel>(SESSION_URL, { withCredentials: true })
                .pipe(
                    map(user => {
                        if (!user) {
                            throw new Error('No session user returned');
                        }
                        return this.processUser(user)
                    }),
                    finalize(() => (this.sessionRequest = null)),
                    shareReplay({bufferSize: 1, refCount: false})
                );
        }
        return this.sessionRequest;
    }

    getIntercomToken() {
        return this.http.get<{ intercomJwt: string }>(INTERCOM_TOKEN_URL, {withCredentials: true})
    }

    getUser(): UserModel | null {
        return this.userSubject.getValue();
    }

    updatePassword(passRecovery: PasswordRecovery): Observable<void> {
        return this.http.put<void>(PASSWORD_UPDATE_URL, passRecovery, {withCredentials: true})
    }

    getMembershipInfoForUpgradePage(email: string): Observable<UpgradeInfoResponse> {
        return this.http
            .get<Partial<UpgradeInfoResponse & Record<string, any>>>(`${USER_INFO_UPGRADE_URL}/${encodeURIComponent(email)}`, { withCredentials: true })
            .pipe(
                tap(user => this.processUser(user as UserModel)),
                map(user => ({
                    id: user.id!,
                    email: user.email!,
                    membershipId: user.membershipId!
                }))
            );
    }

    getOtpOptions(email: string): Observable<OtpOptionsResponse> {
        return this.http.post<OtpOptionsResponse>(
            `${AUTH_URL}/otp/options`,
            { email }
        );
    }

    requestOtp(email: string, channel: OtpChannel): Observable<void> {
        return this.http.post<void>(`${AUTH_URL}/otp/request`, { email, channel });
    }

    verifyOtp(email: string, code: string): Observable<UserModel> {
        return this.http
            .post<any>(
                `${AUTH_URL}/otp/verify`,
                { email, code },
                { withCredentials: true }
            )
            .pipe(map((res) => this.processUser({...res.user, token: res.token})));
    }
}

/**
 * Shallow-merge current user with a *partial* update:
 * - Copies only non-null/undefined keys from `next` into `prev`.
 * - Preserves existing fields when the partial omits them.
 * - Wraps the result in `new UserModel(...)` so getters (isPaid, upgradeLink, etc.) work.
 */
function mergeUserSafe(prev: UserModel | null, next: Partial<UserModel>): UserModel {
    const base: any = prev ? { ...prev } : {};
    for (const [k, v] of Object.entries(next)) {
        if (v !== null && v !== undefined) {
            base[k] = v;
        }
    }
    return new UserModel(base);
}
